<!-- Optimized MinLivsstilHeader.vue -->
<script setup lang="ts">
import { computed, inject, shallowRef, markRaw } from 'vue';
import { useStore } from '@/stores/main';
import { useIonRouter } from '@ionic/vue';
import BProgressBar from '@/components/BProgressBar.vue';
import BSubpageIcon from '@/components/BSubpageIcon.vue';
import Star from '@/components/icons/Star.vue';
import Globe from '@/components/illustrations/Globe.vue';

const props = defineProps({
  currentSectionIdx: { type: Number, default: 0 },
  currentPageIdx: { type: Number, default: 0 },
  questionCount: { type: Number, default: 0 },
  sections: { type: Array, default: () => [] },
  showProgress: { type: Boolean, default: false },
  isResultsPage: { type: Boolean, default: false },
});

const emit = defineEmits<{
  (e: 'slide', value: number): void;
}>();

// Injected values
const currentSection = inject('currentSection');
const subpageData = inject('subpageData', '');
const colorScheme = inject('colorScheme', 'green');
const setSection = inject('setSection');

const store = useStore();
const router = useIonRouter();

// Memoized color configurations
const COLOR_CONFIG = markRaw({
  header: {
    purple: 'bg-purple-light',
    yellow: 'bg-yellow-light',
    green: 'bg-green-light',
    blue: 'bg-blue-light',
  },
  text: {
    purple: 'text-purple-darker',
    yellow: 'text-yellow-darker',
    green: 'text-green-darker',
    blue: 'text-blue-darker',
  },
});

// Optimized computed properties
const sectionIcons = computed(() => {
  if (!props.sections?.length) return [];
  
  return props.sections.map((section: any, idx: number) => ({
    idx: idx + 1,
    icon: section.pageName,
  }));
});

const sectionChecks = computed(() => {
  if (!props.sections?.length) return [];
 
  const profileFactors = store.profile?.factors;
  if (!profileFactors) return new Array(props.sections.length).fill(false);
 
  return props.sections.map((section: any) => {
    // Only consider the first question (group) in each section that has a category_id
    const firstGroupWithFactor = section.groups.find(
      (group: any) => group.category_id != null && group.category_id !== 0
    );
   
    // If no valid group found, return false
    if (!firstGroupWithFactor) return false;
   
    // Check if the first question has been answered
    const factor = profileFactors[firstGroupWithFactor.category_id];
    return factor?.value != null && factor.value !== '';
  });
});

const headerColor = computed(() => 
  COLOR_CONFIG.header[colorScheme as keyof typeof COLOR_CONFIG.header]
);

const textColor = computed(() => 
  COLOR_CONFIG.text[colorScheme as keyof typeof COLOR_CONFIG.text]
);

const headerHeight = computed(() => 
  props.currentSectionIdx > 0 ? 'h-[172px]' : 'h-[112px]'
);

// Optimized event handlers
const handleBarSlide = (e: any) => emit('slide', e);

const handleSectionClick = (idx: number) => {
  if (typeof setSection === 'function') {
    setSection(idx);
  } else {
    console.error('setSection is not a function');
  }
};

// Optimized logout function
const logout = async () => {
  // Clear all storage in one batch
  try {
    // Clear storages
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear cookies more efficiently
    const expiredDate = 'Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      document.cookie = `${name}=;expires=${expiredDate};path=/`;
      document.cookie = `${name}=;expires=${expiredDate};path=/;domain=${window.location.hostname}`;
    });
    
    // Logout from store
    await store.logout();
    
    // Navigate to login
    router.replace('/login');
  } catch (error) {
    console.error('Logout error:', error);
    // Fallback navigation
    router.replace('/login');
  }
};

// Button state for logout button
const isLoggingOut = shallowRef(false);

const handleLogout = async () => {
  if (isLoggingOut.value) return;
  
  isLoggingOut.value = true;
  try {
    await logout();
  } finally {
    isLoggingOut.value = false;
  }
};
</script>

<template>
  <div class="transition-[height]">
    <div
      :class="[headerHeight, headerColor]"
      class="transition-[height] relative top-0 w-full z-10"
    >
      <div
        class="grid grid-rows-1 grid-cols-[0.2fr_3fr_0.4fr] items-center h-[112px]"
      >
        <span class="text-4xl text-white font-black place-self-start pl-6 pt-8">
          2.
        </span>
        <div :class="textColor" class="text-center pl-8">
          <h1 class="m-0 text-[20px] lg:text-start lg:text-[30px]">
            Min livsstil
          </h1>
          <h2
            v-if="currentSection"
            class="m-0 text-sm lg:mt-[-4%] lg:text-lg sm:mb-5"
            v-text="currentSection.pageLabel"
          />
        </div>
        <component 
          :is="currentSectionIdx === 0 ? Globe : 'button'"
          v-if="currentSectionIdx === 0"
          @click="currentSectionIdx > 0 ? handleLogout : null"
          :disabled="isLoggingOut"
          :class="[
            currentSectionIdx > 0 && 'py-2 px-4 md:max-w-20 mr-3 rounded bg-[#8d3e79] text-white transition-all duration-200',
            currentSectionIdx > 0 && (isLoggingOut ? 'opacity-50 cursor-not-allowed' : 'hover:bg-black focus:outline-none focus:ring-2 focus:ring-purple-400 active:bg-[#6b2c5b]')
          ]"
        >
          {{ currentSectionIdx > 0 ? (isLoggingOut ? 'Logging out...' : 'Logout') : '' }}
        </component>
        <button 
          v-else
          @click="handleLogout"
          :disabled="isLoggingOut"
          class="py-2 px-4 md:max-w-20 mr-3 rounded bg-[#8d3e79] text-white transition-all duration-200"
          :class="isLoggingOut ? 'opacity-50 cursor-not-allowed' : 'hover:bg-black focus:outline-none focus:ring-2 focus:ring-purple-400 active:bg-[#6b2c5b]'"
        >
          {{ isLoggingOut ? 'Logging out...' : 'Logout' }}
        </button>
      </div>
      
      <div
        v-show="currentSectionIdx > 0"
        class="flex justify-center lg:gap-x-2 gap-x-0 -mt-9 mb-2 lg:mr-[100px] pt-[10px]"
      >
        <button
          v-for="(section, index) in sectionIcons"
          :key="`section-${section.idx}`"
          @click="handleSectionClick(section.idx)"
          class="bg-transparent border-0 p-0"
        >
          <b-subpage-icon
            :icon="section.icon"
            :active="currentSectionIdx === section.idx"
            :small="true"
            :header="true"
            :checked="sectionChecks[index]"
            class="icon-small"
          />
        </button>
        <button 
          @click="handleSectionClick(sections.length + 1)"
          class="bg-transparent border-0 p-0"
        >
          <b-subpage-icon
            icon="info"
            :small="true"
            :header="true"
            :active="currentSectionIdx === sections.length + 1"
            class="icon-small"
          />
        </button>
      </div>
      
      <div 
        v-if="currentSectionIdx > 0 && showProgress"
        class="px-8"
      >
        <b-progress-bar
          :current="currentPageIdx"
          :max="questionCount"
          @slide="handleBarSlide"
          interactive
        />
      </div>
      
      <div
        v-show="isResultsPage"
        class="flex justify-center items-center mt-5 text-sm tracking-[0.5rem] font-medium uppercase text-purple-darker relative"
      >
        <star class="mr-4" />
        <span class="relative top-[1px]">Resultat</span>
        <star class="ml-2" />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* CSS containment for better performance */
.transition-[height] {
  contain: layout style;
}

/* Icon scaling for small screens */
@media (max-width: 640px) {
  .icon-small {
    transform: scale(0.8);
  }
}

/* Optimize button interactions */
button {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

/* Use CSS variables for consistent transitions */
:root {
  --transition-duration: 200ms;
  --transition-timing: ease-out;
}

.transition-all {
  transition-duration: var(--transition-duration);
  transition-timing-function: var(--transition-timing);
}

/* Reduce repaints with will-change */
.transition-[height] {
  will-change: height;
}

/* Hardware acceleration for smoother animations */
.bg-transparent {
  backface-visibility: hidden;
  transform: translateZ(0);
}
</style>